<template>
  <button
    class="generic-btn"
    :class="buttonClasses"
    :disabled="disabled || isLoading"
    @click.stop="emit('click')"
  >
    <LoadingSpinner v-if="isLoading" class="spinner" :size="'mini'" :is-mini-white="isSpinnerWhite" />
    <template v-if="!isLoading && hasIcon">
      <img v-if="iconCmp" :src="iconCmp" class="icon" alt="">
      <IconWrapperWithOptions v-else-if="iconOptions && iconName" v-bind="{ ...iconOptions, name: iconName }" />
      <IconWrapper v-else-if="iconName" :name="iconName" />
    </template>
    <Typography
      v-if="$slots.default || text"
      :type="typographyType"
      as="span"
      class="btn-text"
    >
      <slot>{{ text }}</slot>
    </Typography>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useViewPort } from '@/composables'
import { ButtonVariant, GenericButtonSize } from '@/styles/types'
import type { IconOptions } from '@/components/common/icons/IconWrapperWithOptions.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import IconWrapper from '@/components/common/icons/IconWrapper.vue'
import IconWrapperWithOptions from '@/components/common/icons/IconWrapperWithOptions.vue'
import Typography from '@/components/common/typography/Typography.vue'
import { TypographyKey } from '@/components/common/typography/types'

export interface GenericButtonProps {
  variant?: ButtonVariant
  size?: GenericButtonSize
  text?: string
  disabled?: boolean
  disableHover?: boolean
  isFullSize?: boolean
  isLoading?: boolean
  iconCmp?: string
  iconName?: string
  iconOptions?: Omit<IconOptions, 'name'>
  iconPosition?: 'left' | 'right'
}

const props = withDefaults(defineProps<GenericButtonProps>(), {
  variant: ButtonVariant.PRIMARY,
  size: GenericButtonSize.LARGE,
  disabled: false,
  isFullSize: false,
  isLoading: false,
  iconPosition: 'left',
})

const emit = defineEmits<{
  click: []
}>()

const { isMobileLayout } = useViewPort()

const hasIcon = computed(() => !!props.iconCmp || !!props.iconName || !!props.iconOptions)

const typographyType = computed(() => {
  switch (props.size) {
    case GenericButtonSize.XSMALL: return TypographyKey.BodyMSemiBold
    case GenericButtonSize.SMALL: return TypographyKey.BodyLSemiBold
    default: return TypographyKey.BodyXlSemiBold
  }
})

const buttonClasses = computed(() => [
  props.variant,
  props.size,
  {
    disabled: props.disabled,
    loading: props.isLoading,
    'no-hover': props.disableHover,
    mobile: isMobileLayout.value,
    'full-size': props.isFullSize,
    'icon-right': props.iconPosition === 'right',
  },
])

const isSpinnerWhite = computed(() =>
  props.variant === ButtonVariant.PRIMARY ||
  props.variant === ButtonVariant.BLACK ||
  props.variant === ButtonVariant.TERTIARY_WHITE
)
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

@mixin hoverable {
  &:not(.no-hover, .disabled, :active):hover {
    @content;
  }
}

.generic-btn {
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  width: fit-content;
  margin: 0;
  cursor: pointer;
  user-select: none;

  // Default size: L (48px height)
  padding: 12px 16px;
  gap: 8px;
  border-radius: 12px;

  .icon,
  .icon-wrapper,
  :deep(.icon-wrapper svg) {
    width: var(--icon-size, 20px);
    height: var(--icon-size, 20px);
  }

  &.loading {
    pointer-events: none;
  }

  &.full-size {
    width: 100%;
  }

  &.icon-right {
    flex-direction: row-reverse;
  }

  // --- Variants ---

  &.primary {
    background-color: $rebranding-primary-500;
    color: $white;

    @include hoverable {
      background-color: $rebranding-primary-600;
    }

    &:not(.disabled):active {
      background-color: $rebranding-primary-650;
    }

    &.disabled:not(.loading) {
      background-color: $rebranding-grey-500;
    }
  }

  &.secondary {
    background-color: $rebranding-primary-50;
    color: $rebranding-primary-500;

    @include hoverable {
      background-color: $rebranding-primary-100;
      color: $rebranding-primary-600;
    }

    &:not(.disabled):active {
      background-color: $rebranding-primary-200;
      color: $rebranding-primary-650;
    }

    &.disabled:not(.loading) {
      background-color: $rebranding-grey-300;
      color: $rebranding-grey-500;
    }
  }

  &.tertiary {
    background-color: transparent;
    color: $rebranding-primary-500;

    @include hoverable {
      background-color: $rebranding-primary-50;
      color: $rebranding-primary-600;
    }

    &:not(.disabled):active {
      color: $rebranding-primary-650;
    }

    &.disabled:not(.loading) {
      color: $rebranding-grey-500;
    }
  }

  &.primary-white {
    background-color: $white;
    color: $rebranding-grey-900;

    @include hoverable {
      background-color: $rebranding-primary-50;
    }

    &:not(.disabled):active {
      background-color: $rebranding-primary-200;
    }

    &.disabled:not(.loading) {
      background-color: $rebranding-grey-400;
      color: $white;
    }
  }

  &.black {
    background-color: $rebranding-grey-900;
    color: $white;

    @include hoverable {
      background-color: $black;
    }

    &:not(.disabled):active {
      background-color: $rebranding-grey-800;
    }

    &.disabled:not(.loading) {
      background-color: $rebranding-grey-500;
    }
  }

  &.tertiary-white {
    background-color: transparent;
    color: $white;

    @include hoverable {
      color: $rebranding-primary-50;
    }

    &:not(.disabled):active {
      color: $rebranding-primary-100;
    }

    &.disabled:not(.loading) {
      color: $rebranding-grey-300;
    }
  }

  // --- Sizes ---
  &.large {
    --icon-size: 20px;
  }

  &.medium {
    --icon-size: 16px;
    padding: 8px 12px;
    gap: 4px;
    border-radius: 8px;
  }

  &.small {
    --icon-size: 16px;
    padding: 6px 8px;
    gap: 4px;
    border-radius: 6px;
  }

  &.xsmall {
    --icon-size: 12px;
    padding: 4px 6px;
    gap: 4px;
    border-radius: 4px;
  }
}
</style>
